import {
  IScheduledTransfer,
  IScheduledTransferDetail,
  IScheduledTransferHistory,
  IScheduledTransferHistoryDetail,
  SchedulePaymentDueType,
} from '@/domains/SchedulePaymentDomain'
import {
  getDifferenceInMinutes,
  getFormattedDate,
  getFormattedDateByJakarta,
  getIsThisMinute,
  getIsToday,
  getIsWithin1Hour,
  ShortWeek,
} from '@/utils/dateUtils'
import { $ } from '@/utils/i18nUtils'

export const INFINITE_TEXT = $('scheduledate_neverend_desc')
export const INFINITE_TIMESTAMP = 253402189200000
export const INFINITE_YEAR = 9999
export const INFINITE = '9999/12/31'

function getEndDate(endDtm: number, format: string) {
  return new Date(endDtm).getFullYear() === INFINITE_YEAR
    ? INFINITE_TEXT
    : getFormattedDateByJakarta(endDtm, format)
}

export function getTransferDate({
  startDtm,
  endDtm,
  dueType,
}:
  | IScheduledTransferHistoryDetail
  | IScheduledTransferDetail
  | IScheduledTransferHistory
  | IScheduledTransfer) {
  if (dueType === SchedulePaymentDueType.DAY) {
    return getFormattedDateByJakarta(startDtm, 'DD MMM YYYY')
  }

  return `${getFormattedDateByJakarta(startDtm, 'DD MMM YYYY')} ~ ${getEndDate(
    endDtm,
    'DD MMM YYYY',
  )}`
}

export function getWeekLang(day: number): string {
  const weekLang = ShortWeek[day]

  return weekLang && weekLang.toLowerCase()
}

export function getFormattedOnce(startDtm: number): string {
  const durationType = $('transferfrequencyoption_once_desc')
  const onceLang = getFormattedDateByJakarta(startDtm, 'DD MMM YYYY')
  if (!onceLang) {
    return $('common_error_code_desc', 9999)
  }

  return `${durationType}, ${onceLang}`
}

export function getFormattedWeek(trsfDay: number): string {
  const durationType = $('transferfrequencyoption_everyweek_desc')
  const weekLang = getWeekLang(trsfDay)

  if (!weekLang) {
    return $('common_error_code_desc', 9999)
  }

  const weekday = $(`common_day_${weekLang}_desc`)

  return `${durationType}, ${weekday}`
}

let language = ''
export function getLanguage(isID: string) {
  return (language = isID)
}

export function getFormattedMonth(trsfDay: number): string {
  const durationType = $('transferfrequencyoption_everymonth_desc')

  if (language === 'id_ID') {
    return `${durationType}, tanggal [${trsfDay}]`
  } else {
    if (trsfDay === 1) {
      return `${durationType}, on ${trsfDay}st day`
    } else if (trsfDay === 2) {
      return `${durationType}, on ${trsfDay}nd day`
    } else if (trsfDay === 3) {
      return `${durationType}, on ${trsfDay}rd day`
    } else {
      return `${durationType}, on ${trsfDay}th day`
    }
  }
}

export function getTransferSchedule({
  dueType,
  trsfDay,
  startDtm,
}:
  | IScheduledTransferHistoryDetail
  | IScheduledTransferDetail
  | IScheduledTransferHistory
  | IScheduledTransfer) {
  return {
    // [SchedulePaymentDueType.DAY]: getFormattedDateByJakarta(startDtm, 'DD MMM YYYY'),
    [SchedulePaymentDueType.DAY]: getFormattedOnce(startDtm),
    [SchedulePaymentDueType.WEEK]: getFormattedWeek(trsfDay),
    [SchedulePaymentDueType.MONTH]: getFormattedMonth(trsfDay),
  }[dueType as SchedulePaymentDueType]
}

export function takeAllExp(pattern: string) {
  return new RegExp(pattern, 'ig')
}

export function removeAllHypens(value: string) {
  return value.replace(takeAllExp('-'), '')
}

export function getFormattedInputCardNo(data: string) {
  return data
    .replace(/[^\d]/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim()
}

export function getFormattedInputExpiredDate(data: string) {
  return data.replace(/[^\d]/g, '').replace(/(.{2})(.{1})/g, '$1/$2')
}

export const getSchedulePaymentStartDt = (startDt: string): string => {
  return getFormattedDate(startDt, 'YYYY/MM/DD')
}

export const getSchedulePaymentEndDt = (
  startDt: string,
  endDt: string,
  dueType?: SchedulePaymentDueType,
): string => {
  // endDt: 예약이체 종료일자 (반복유형코드 - D: YYYY/MM/DD (예약이체 시작일자와 동일), W: YYYY/MM/DD, M: YYYY/MM/31 (매월 말일), Never End 설정 시 9999/12/31로 설정해서 전달필요)
  switch (dueType) {
    case SchedulePaymentDueType.DAY:
      return getFormattedDate(startDt, 'YYYY/MM/DD')
    case SchedulePaymentDueType.MONTH:
    case SchedulePaymentDueType.WEEK:
    default:
      return getFormattedDate(endDt, 'YYYY/MM/DD')
  }
}

export const getNotificationFormattedDate = (
  timestamp: number,
  translator: (value: string, data?: string[]) => string,
  now?: Date,
) => {
  if (getIsThisMinute(timestamp)) {
    return translator('common_now_desc')
  }

  if (getIsWithin1Hour(timestamp, now)) {
    return translator('common_fewminutesago_desc', [
      `${getDifferenceInMinutes(new Date(), timestamp)}`,
    ])
  }

  if (getIsToday(timestamp)) {
    return getFormattedDateByJakarta(timestamp, 'HH:mm')
  }

  return getFormattedDateByJakarta(timestamp, 'DD MMM HH:mm')
}

export function getMaskedDebitCardNo(data: string) {
  const matchedNo = data && data.match(/(\d{4})(\d{4})(\d{4})(\d{4})/)

  if (!matchedNo) {
    return ''
  }

  return `${matchedNo[1]}-${matchedNo[2].slice(0, 2)}**-****-${matchedNo[4]}`
}

export function getMaskedCustomerName(data: string, startIndex: number) {
  const masked = data.slice(startIndex).replace(/[\s\S]/g, '*')
  return data.slice(0, 3) + masked
}

export function getFormattedYearMonth(year: number, month: number, givenSeparator: string = '/') {
  const paddedMonth = month.toString().padStart(2, '0')

  return `${year}${givenSeparator}${paddedMonth}`
}

export function getLastOf(text: string, number: number) {
  const length = text.length

  return text.substring(length - number, length)
}

export const getFormattedCardNo = (withoutDash: string): string => {
  const matchedNo = withoutDash.match(new RegExp('.{1,4}', 'g')) // 0000000000000000 -> [0000,0000,0000,0000]

  if (!matchedNo) {
    return ''
  }

  return matchedNo.join('-') // [0000,0000,0000,0000] -> 0000-0000-0000-0000
}
